<template>
  <v-dialog
    :value="value"
    width="560"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-card-title class="text-subtitle-1">
        週予定をまとめて登録
      </v-card-title>
      <v-card-text>
        <v-select
          v-model="selectedCastId"
          :items="casts"
          item-text="name"
          item-value="id"
          label="キャスト"
          dense
          hide-details
          class="mb-4 pt-1"
        />
        <p class="text-caption dialog-muted mb-3">
          チェックした日にシフトを登録・更新します。チェックを外すと却下（取下げ）されます。指名予約がある日は変更できません
        </p>
        <template v-if="selectedCastId">
          <div
            v-for="dayRow in rows"
            :key="`cw-${dayRow.date}`"
            class="cast-week-row"
          >
            <v-simple-checkbox
              v-model="dayRow.checked"
              :disabled="dayRow.locked"
              :ripple="false"
            />
            <span class="cast-week-row__date">{{ formatDate(dayRow.date) }}</span>
            <v-text-field
              v-model="dayRow.startTime"
              type="time"
              dense
              hide-details
              :disabled="dayRow.locked || !dayRow.checked"
              class="cast-week-row__time"
            />
            <span class="dialog-muted">〜</span>
            <v-text-field
              v-model="dayRow.endTime"
              type="time"
              dense
              hide-details
              :disabled="dayRow.locked || !dayRow.checked"
              class="cast-week-row__time"
            />
            <v-chip
              v-if="dayRow.locked"
              x-small
              outlined
              color="error"
              title="指名予約があるため変更できません"
            >
              予約あり
            </v-chip>
          </div>
        </template>
        <p
          v-else
          class="dialog-muted text-caption my-4"
        >
          キャストを選択してください
        </p>
        <p
          v-if="error"
          class="error--text text-caption mt-3 mb-0"
        >
          {{ error }}
        </p>
      </v-card-text>
      <v-card-actions class="px-6 pb-4">
        <v-spacer></v-spacer>
        <v-btn
          text
          :disabled="submitting"
          @click="$emit('input', false)"
        >
          キャンセル
        </v-btn>
        <v-btn
          color="primary"
          :ripple="false"
          :loading="submitting"
          :disabled="!selectedCastId"
          @click="save"
        >
          まとめて登録
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  ref, computed, watch, getCurrentInstance,
} from '@vue/composition-api'
import { format } from 'date-fns'
import WorkScheduleApi from '@/api/waiter/WorkSchedule'
import { buildShiftDateTime } from '@/views/waiter/shiftStaffingUtils'
import { buildCastWeekOps } from '@/views/waiter/castWeekOps'

export default {
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // 選択可能なキャスト一覧（{id, name}）
    casts: {
      type: Array,
      default: () => [],
    },
    // 開いたとき最初に選択しておくキャストID（チップ経由）。null なら未選択で開く
    initialCastId: {
      type: Number,
      default: null,
    },
    // 週の7日分の日付（yyyy-MM-dd）
    weekDates: {
      type: Array,
      default: () => [],
    },
    // 週の全シフト（{id, date, userId, startAt, endAt} の配列）
    schedules: {
      type: Array,
      default: () => [],
    },
    // 日付 => 指名予約があるキャストの userId 配列（ロック判定用）
    nominatedUserIdsByDate: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const selectedCastId = ref(null)
    const rows = ref([])
    const error = ref('')
    const submitting = ref(false)

    const selectedCast = computed(() => props.casts.find(c => Number(c.id) === Number(selectedCastId.value)) || null)

    const buildRows = () => {
      if (!selectedCastId.value) {
        rows.value = []

        return
      }
      const castId = Number(selectedCastId.value)
      const schedulesByDate = props.schedules
        .filter(s => Number(s.userId) === castId)
        .reduce((acc, s) => {
          acc[String(s.date).slice(0, 10)] = s

          return acc
        }, {})

      rows.value = props.weekDates.map(date => {
        const schedule = schedulesByDate[date] || null
        const locked = (props.nominatedUserIdsByDate[date] || []).map(Number).includes(castId)
        const toTime = value => (value ? format(new Date(value), 'HH:mm') : '')

        return {
          date,
          checked: !!schedule,
          locked,
          startTime: schedule ? toTime(schedule.startAt) : '',
          endTime: schedule ? toTime(schedule.endAt) : '',
          existing: schedule
            ? { id: schedule.id, startTime: toTime(schedule.startAt), endTime: toTime(schedule.endAt) }
            : null,
        }
      })
    }

    watch(() => props.value, isOpen => {
      if (!isOpen) return
      error.value = ''
      selectedCastId.value = props.initialCastId
      buildRows()
    })

    // ダイアログ内でキャストを切り替えたら行を作り直す
    watch(selectedCastId, () => {
      error.value = ''
      buildRows()
    })

    const formatDate = dateStr => {
      const date = new Date(dateStr)
      const dayNames = ['日', '月', '火', '水', '木', '金', '土']

      return `${date.getMonth() + 1}/${date.getDate()}（${dayNames[date.getDay()]}）`
    }

    const save = async () => {
      error.value = ''
      const ops = buildCastWeekOps(rows.value)
      if (!ops.ok) {
        error.value = ops.error

        return
      }
      if (ops.creates.length === 0 && ops.updates.length === 0 && ops.rejects.length === 0) {
        emit('input', false)

        return
      }

      submitting.value = true
      const failed = []
      try {
        // 1件ずつ実行し、失敗した日はスキップして続行する（ShiftBulkAddForm と同方式）
        /* eslint-disable no-await-in-loop, no-restricted-syntax */
        for (const op of ops.creates) {
          try {
            const payload = { date: op.date, userId: Number(selectedCastId.value) }
            if (op.startTime && op.endTime) {
              payload.startAt = buildShiftDateTime(op.date, op.startTime)
              payload.endAt = buildShiftDateTime(op.date, op.endTime, op.startTime)
            }
            await WorkScheduleApi.createWorkSchedule(payload)
          } catch (e) {
            failed.push(`${formatDate(op.date)} の登録`)
          }
        }
        for (const op of ops.updates) {
          try {
            const payload = { id: op.id }
            if (op.startTime && op.endTime) {
              payload.startAt = buildShiftDateTime(op.date, op.startTime)
              payload.endAt = buildShiftDateTime(op.date, op.endTime, op.startTime)
            } else {
              // 両方空 → 時間未定に戻す
              payload.startAt = ''
              payload.endAt = ''
            }
            await WorkScheduleApi.updateWorkSchedule(payload)
          } catch (e) {
            failed.push(`${formatDate(op.date)} の変更`)
          }
        }
        for (const id of ops.rejects) {
          try {
            await WorkScheduleApi.rejectWorkSchedule(id)
          } catch (e) {
            const dayRow = rows.value.find(r => r.existing?.id === id)
            failed.push(`${dayRow ? formatDate(dayRow.date) : ''} の却下`)
          }
        }
        /* eslint-enable no-await-in-loop, no-restricted-syntax */

        const total = ops.creates.length + ops.updates.length + ops.rejects.length
        const okCount = total - failed.length
        const castName = selectedCast.value?.name || ''
        if (okCount > 0) vm.$toast.success(`${castName} の週予定を更新しました（${okCount}件）`)
        if (failed.length > 0) vm.$toast.warning(`一部の操作に失敗しました: ${failed.join('・')}`)

        emit('input', false)
        emit('saved')
      } finally {
        submitting.value = false
      }
    }

    return {
      selectedCastId,
      rows,
      error,
      submitting,
      formatDate,
      save,
    }
  },
}
</script>

<style scoped>
.cast-week-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 0;
  border-bottom: 1px dashed rgba(148, 163, 184, 0.2);
}

.cast-week-row__date {
  min-width: 84px;
  font-size: 13px;
  font-weight: 600;
}

.cast-week-row__time {
  max-width: 110px;
  margin-top: 0;
  padding-top: 0;
}

.dialog-muted {
  color: #94a3b8;
}
</style>
